/**
 * Onde o painel do dado abre e como se arrasta (E203.F2, ADR-0146 §3): ao lado do bloco clicado
 * (à direita se cabe, senão à esquerda, senão por cima), sempre dentro da janela; a barra do alto
 * arrasta com o ponteiro (mouse, toque ou caneta), sem biblioteca.
 *
 * E203.F2.C05: o ponteiro que desce num controle da barra (o "Fechar") não começa o arrasto. Com a
 * captura do ponteiro na barra, o navegador entrega o `click` à barra e o botão nunca o recebe.
 *
 * E209.F2.T06 (GUS-296 item 10): a altura vai da posição ao rodapé da janela (não 80% fixo a partir
 * de qualquer ponto), e o bloco perto do rodapé abre o painel mais acima, para o conteúdo — e o
 * botão de registrar no fim dele — caber e rolar dentro da janela.
 */
import { useCallback, useRef, useState, type PointerEvent } from 'react';

export const LARGURA_DO_PAINEL = 416;
const MARGEM = 12;
const ALTURA_MINIMA = 160;
/** A altura que o painel pode ocupar (a de antes: 80% da janela). */
const FRACAO_DA_JANELA = 0.8;
/** Abaixo disto até o rodapé, o painel sobe: o formulário da revisão não cabe numa faixa estreita. */
const ALTURA_CONFORTAVEL = 480;

export interface Posicao {
  x: number;
  y: number;
}

function limitar(valor: number, minimo: number, maximo: number): number {
  return Math.min(Math.max(valor, minimo), Math.max(minimo, maximo));
}

/** Dentro da janela: a largura inteira e pelo menos a barra do alto à vista. */
export function dentroDaJanela(p: Posicao): Posicao {
  return {
    x: limitar(p.x, MARGEM, window.innerWidth - LARGURA_DO_PAINEL - MARGEM),
    y: limitar(p.y, MARGEM, window.innerHeight - ALTURA_MINIMA),
  };
}

/** A altura que cabe da posição até o rodapé da janela, até 80% dela; o conteúdo rola dentro. */
export function alturaMaxima(p: Posicao): number {
  const ate_o_rodape = window.innerHeight - p.y - MARGEM;
  return Math.max(0, Math.min(Math.round(window.innerHeight * FRACAO_DA_JANELA), ate_o_rodape));
}

/** Sem a altura confortável até o rodapé, o painel sobe até tê-la (alinhado ao rodapé). */
function cabendoNaJanela(p: Posicao): Posicao {
  const confortavel = Math.min(ALTURA_CONFORTAVEL, window.innerHeight * FRACAO_DA_JANELA);
  if (window.innerHeight - p.y - MARGEM >= confortavel) return p;
  return { x: p.x, y: Math.max(MARGEM, Math.floor(window.innerHeight - MARGEM - confortavel)) };
}

/** Ao lado do elemento: à direita, à esquerda ou, sem espaço dos lados, logo abaixo dele. */
export function posicaoInicial(elemento: HTMLElement | null): Posicao {
  if (elemento === null) return dentroDaJanela({ x: window.innerWidth, y: MARGEM * 6 });
  const r = elemento.getBoundingClientRect();
  if (r.right + MARGEM + LARGURA_DO_PAINEL <= window.innerWidth) {
    return dentroDaJanela(cabendoNaJanela({ x: r.right + MARGEM, y: r.top }));
  }
  if (r.left - MARGEM - LARGURA_DO_PAINEL >= 0) {
    return dentroDaJanela(cabendoNaJanela({ x: r.left - MARGEM - LARGURA_DO_PAINEL, y: r.top }));
  }
  return dentroDaJanela(cabendoNaJanela({ x: r.left, y: r.bottom + MARGEM }));
}

/** O ponteiro desceu num controle (botão, link ou campo), não na barra em si. */
function emControle(alvo: EventTarget): boolean {
  return (
    alvo instanceof Element && alvo.closest('button, a[href], input, select, textarea') !== null
  );
}

export function usePosicaoDoPainel(elemento: HTMLElement | null) {
  const [posicao, setPosicao] = useState<Posicao>(() => posicaoInicial(elemento));
  const arrasto = useRef<{ dx: number; dy: number } | null>(null);

  const comecar = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      if (e.button !== 0 || emControle(e.target)) return;
      arrasto.current = { dx: e.clientX - posicao.x, dy: e.clientY - posicao.y };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [posicao],
  );
  const mover = useCallback((e: PointerEvent<HTMLElement>) => {
    const a = arrasto.current;
    if (a === null) return;
    setPosicao(dentroDaJanela({ x: e.clientX - a.dx, y: e.clientY - a.dy }));
  }, []);
  const soltar = useCallback((e: PointerEvent<HTMLElement>) => {
    arrasto.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, []);

  return {
    posicao,
    alca: { onPointerDown: comecar, onPointerMove: mover, onPointerUp: soltar },
  };
}
